import { DEV_MODE } from '../../global/env';
import { isNil, notEqual } from '../../utils/unit';
import { DisposalBin, listen } from '../events';
import { Logger } from '../logger';
export class ContextProviderController {
    constructor(_host, initialValue, _options) {
        this._host = _host;
        this.initialValue = initialValue;
        this._options = _options;
        this._isProviding = false;
        this._consumers = new Set();
        /* c8 ignore start */
        if (DEV_MODE && _options.name) {
            this._logger = new Logger(_host, {
                name: `🧵 ${String(this.name)}`,
                owner: this
            });
        }
        /* c8 ignore stop */
        this._stopDisposal = new DisposalBin(_host, 
        /* c8 ignore next */
        DEV_MODE && { name: 'contextProviderStopDisposal', owner: this });
        this._value = initialValue;
        if (_host instanceof Element)
            this.setRef(_host);
        _host.addController(this);
    }
    get id() {
        return this._options.id;
    }
    get name() {
        return this._options.name;
    }
    get value() {
        return this._value;
    }
    set value(newValue) {
        if (notEqual(newValue, this._value)) {
            this._value = newValue;
            this._updateConsumers();
        }
    }
    /**
     * Whether the provider has attached to an element and is providing the context.
     */
    get isConnected() {
        return this._isProviding;
    }
    hostConnected() {
        var _a, _b;
        this.start();
        (_b = (_a = this._options).onConnect) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    hostDisconnected() {
        var _a, _b;
        // Reset value on disconnect.
        this.value = this.initialValue;
        this.stop(false);
        (_b = (_a = this._options).onDisconnect) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    /**
     * Set a reference to a DOM element that this controller will use to provide the context from
     * by listening to consumer connect events at the given reference.
     */
    setRef(newRef) {
        if (this._ref !== newRef) {
            this._handleRefChange(newRef);
        }
    }
    _handleRefChange(newRef) {
        const consumers = new Set(this._consumers);
        this.stop(false);
        this._ref = newRef;
        this.start();
        consumers.forEach((consumer) => consumer.reconnect());
    }
    /**
     * Start providing context to consumers.
     */
    start() {
        if (this._isProviding || isNil(this._ref)) {
            return;
        }
        const dispose = listen(this._ref, 'vds-context-consumer-connect', this._handleConsumerConnect.bind(this));
        this._stopDisposal.add(dispose);
        this._isProviding = true;
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('started');
        }
        /* c8 ignore stop */
    }
    /**
     * Stop providing context to consumers.
     */
    stop(shouldConsumersReconnect = true) {
        if (!this._isProviding && this._consumers.size === 0)
            return;
        const consumers = new Set(this._consumers);
        this._stopDisposal.empty();
        this._consumers.clear();
        this._isProviding = false;
        if (shouldConsumersReconnect) {
            consumers.forEach((consumer) => consumer.reconnect());
        }
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('stopped');
        }
        /* c8 ignore stop */
    }
    _handleConsumerConnect(event) {
        const consumer = event.detail;
        // Validate event was dispatched by a pairable consumer.
        if (consumer.id !== this.id)
            return;
        // Stop propagation of the event to prevent pairing with similar context providers.
        event.stopImmediatePropagation();
        consumer.onConnect();
        consumer.onUpdate(this._value);
        consumer.onDisconnect(() => {
            this._consumers.delete(consumer);
        });
        this._consumers.add(consumer);
    }
    _updateConsumers() {
        var _a, _b;
        if (!this._isProviding)
            return;
        /* c8 ignore start */
        if (DEV_MODE && this.name) {
            this._logger.debug('updating to', this._value);
        }
        /* c8 ignore stop */
        this._consumers.forEach((consumer) => {
            consumer.onUpdate(this._value);
        });
        (_b = (_a = this._options).onUpdate) === null || _b === void 0 ? void 0 : _b.call(_a, this._value);
    }
}
